import React from 'react'
import { getStatusColor } from 'constants/status'
import { request } from 'utilities/request'
import { getSelectOptions, showDate } from 'utilities/form'
import { textFilter, dateFilter, selectFilter } from 'utilities/filter'
import { getLimit, getPagination } from 'utilities/pagination'
import { LinkButton, StatusChip, Link } from 'components/core'
import { MdEdit } from 'react-icons/md'
import { renderDeleteButton } from 'utilities/app'
import { handleDelete } from 'actions/ticket'
import { FILTER_SET } from 'constants/actionType'

export const filters = (data) => [
  {
    id: 'id',
    label: 'field.ticketId',
    input: textFilter,
  },
  {
    id: 'transDate',
    label: 'field.date',
    input: dateFilter,
    // defaultValue: [getDate(), addDays(getDate(), 1)],
  },
  {
    id: 'repairId',
    label: 'sell.field.repairId',
    input: textFilter,
  },
  {
    id: 'techId',
    label: 'field.technician',
    input: (props) =>
      selectFilter({
        ...props,
        isSearchable: true,
        options: getSelectOptions(data.techs),
      }),
  },
  {
    id: 'partName',
    label: 'field.partName',
    input: textFilter,
  },
  {
    id: 'sku',
    label: 'field.sku',
    input: textFilter,
  },
]

export const columns = ({ session, app, message, action }) => [
  {
    id: 'id',
    label: 'field.ticketId',
    format: ['html', 'excel'],
    renderHtml: ({ row }) => (
      <Link variant="primaryLink" href={`/sell/${row.id}/view`}>
        {row.id}
      </Link>
    ),
  },
  {
    id: 'repairId',
    label: 'sell.field.repairId',
    format: ['html', 'excel'],
    render: ({ row }) => row.extra?.repairId,
  },
  {
    id: 'transDate',
    label: 'field.date',
    format: ['html', 'excel'],
    render: ({ row }) => showDate(row.transDate),
  },
  {
    id: 'fromLocationName',
    label: 'field.technician',
    format: ['html', 'excel'],
  },
  // {
  //   id: 'receiptNo',
  //   label: 'sell.field.receiptNo',
  //   format: ['html', 'excel'],
  //   render: ({ row }) => row.extra?.receiptNo,
  // },
  {
    id: 'productVariantName',
    label: 'field.partName',
    format: ['excel'],
  },
  {
    id: 'sku',
    label: 'field.sku',
    format: ['excel'],
  },
  {
    id: 'quantity',
    label: 'field.quantity',
    format: ['excel'],
  },
  {
    id: 'price',
    label: 'field.price',
    format: ['excel'],
  },
  {
    id: 'memo',
    label: 'field.memo',
    format: ['excel'],
    render: ({ row }) => row.extra?.memo,
  },
  {
    id: 'status',
    label: 'field.status',
    format: ['html', 'excel'],
    renderHtml: ({ row }) => (
      <StatusChip
        label={`status.${row.status}`}
        color={getStatusColor(row.status)}
      />
    ),
    render: ({ row }) => message({ id: `status.${row.status}` }),
  },
  {
    id: 'actions',
    align: 'right',
    renderHtml: ({ row }) => (
      <>
        <LinkButton
          mr={2}
          disabled={row.status === 'INACTIVE'}
          variant="icon"
          icon={<MdEdit />}
          href={`/sell/${row.id}/edit`}
        />
        {renderDeleteButton({
          module: 'sell',
          session,
          status: row.status,
          onSubmit: () =>
            handleDelete('partSell', {
              session,
              app,
              id: row.id,
              hash: row.hash,
              action,
            }),
        })}
      </>
    ),
  },
]

export const handlers = ({
  session,
  app,
  message,
  setState,
  setFilterValues,
  setPagination,
  setTechs,
}) => ({
  handleLoad: async ({ pagination, filterValues } = {}) => {
    const filterId = 'sellList'
    if (!filterValues) {
      filterValues = session.state.filterMap[filterId] || []
      pagination = session.state.paginationMap[filterId] || []
    }
    const resp = await getData({ session, app, pagination, filterValues })
    setFilterValues(filterValues)
    setTechs(resp.techs)
    setState(resp.data)
    setPagination(resp.pagination)
    session.dispatch({
      type: FILTER_SET,
      key: filterId,
      filterValues,
      pagination,
    })
  },
  handlerExport: async ({ filterValues, createExcel }) => {
    const title = 'sell.title.list'
    const cols = columns({ message }).filter(({ format = [] }) =>
      format.includes('excel'),
    )
    const rows = await getExportData({ session, app, filterValues })
    createExcel({ message, title, cols, rows })
  },
})

async function getData({ session, app, pagination = {}, filterValues = [] }) {
  const variables = {
    locationInput: { type: ['TECHNICIAN'] },
    input: getDataInput({ pagination, filterValues }),
  }
  const query = `
    query($input: TicketQueryInput, $locationInput: LocationQueryInput) {
      locations(input: $locationInput) {
        id
        name
      }
      partSellTicketCount(input: $input)
      partSellTickets(input: $input) {
        id
        ticketNo
        fromLocationName
        transDate
        extra
        status
        hash
      }
    }
  `
  const [ok, data] = await request({ query, variables }, { session, app })
  if (!ok) return {}

  const { page, countPerPage } = pagination
  return {
    techs: data.locations,
    data: data.partSellTickets,
    pagination: getPagination(page, countPerPage, data.partSellTicketCount),
  }
}

async function getExportData({ session, app, filterValues }) {
  const variables = { input: getDataInput({ filterValues, joinItem: true }) }
  const query = `
    query($input: TicketQueryInput) {
      partSellTickets(input: $input) {
        id
        ticketNo
        fromLocationName
        productVariantName
        sku
        quantity
        price
        extra
        transDate
        status
      }
    }
  `
  const [ok, data] = await request({ query, variables }, { session, app })
  if (!ok) return []

  return data.partSellTickets.map((item) => {
    const { extra } = item
    item.billingName = extra.billCompany?.name
    item.memo = extra.memo
    return item
  })
}

function getDataInput({ filterValues, pagination, joinItem = false }) {
  const id = filterValues.find(({ id }) => id === 'id')
  const transDate = filterValues.find(({ id }) => id === 'transDate')
  const repairId = filterValues.find(({ id }) => id === 'repairId')
  const techId = filterValues.find(({ id }) => id === 'techId')
  const partName = filterValues.find(({ id }) => id === 'partName')
  const sku = filterValues.find(({ id }) => id === 'sku')
  const input = {
    joinItem,
    id: id?.value,
    transDate: transDate?.value,
    fromLocationId: techId?.value.value,
    productVariantName: partName?.value,
    sku: sku?.value,
    extra: {
      repairId: repairId?.value,
    },
    orderBy: [{ key: 'id', sort: 'DESC' }],
  }
  if (pagination) input.limit = getLimit(pagination)
  return input
}
